<template>
  <div class="collection-landing-shell">
    <div class="collection-landing-head" :class="accentClass">
      <div>
        <label class="active teal-text">{{ moduleName }} Workspace</label>
        <h5>{{ heading }}</h5>
        <p>{{ description }}</p>
      </div>

      <span class="scope-chip" :class="accentClass">
        {{ scopeLabel }}
        <em>{{ activeCount }} active workspaces</em>
      </span>
    </div>

    <div class="workflow-grid">
      <div
        class="workflow-card"
        :class="[accentClass, { 'no-motion': disableMotion }]"
        v-for="tab in operationalTabs"
        :key="tab.id"
        @click="handleActive(tab)"
      >
        <span class="workflow-icon">
          <i class="material-icons">{{ cardIcon(tab) }}</i>
        </span>
        <div class="workflow-copy">
          <small>{{ moduleName }}</small>
          <strong>{{ tab.title }}</strong>
          <p>{{ cardDescription(tab) }}</p>
        </div>
        <span class="workflow-action">
          Open
          <i class="material-icons">chevron_right</i>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "CollectionModuleLandingComponent",
  props: {
    moduleName: { type: String, required: true },
    heading: { type: String, required: true },
    description: { type: String, required: true },
    scopeLabel: { type: String, required: true },
    accent: { type: String, default: "teal" },
    tabs: { type: Array, required: true },
    cardCopy: { type: Object, default: () => ({}) },
    disableMotion: { type: Boolean, default: false },
  },
  emits: ["active"],
  setup(props, context) {
    const operationalTabs = computed(() => {
      return props.tabs.filter((tab) => !tab.title?.toLowerCase().includes("home"));
    });

    const activeCount = computed(() => operationalTabs.value.length);

    const accentClass = computed(() => `accent-${props.accent}`);

    const cardDescription = (tab) => {
      return props.cardCopy[tab.title]?.description || "Open this workspace to review and complete the assigned operational queue.";
    };

    const cardIcon = (tab) => {
      return props.cardCopy[tab.title]?.icon || "work_outline";
    };

    const handleActive = (tab) => {
      context.emit("active", tab);
    };

    return {
      operationalTabs,
      activeCount,
      accentClass,
      cardDescription,
      cardIcon,
      handleActive,
    };
  },
};
</script>

<style scoped>
.collection-landing-shell {
  color: #263238;
  display: grid;
  gap: 18px;
  margin-top: 0;
}

.workflow-grid {
  display: grid;
  gap: 0;
  grid-template-columns: 1fr;
}

.collection-landing-head,
.workflow-card {
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  box-shadow: none;
}

.collection-landing-head {
  align-items: flex-end;
  background: #ffffff;
  border-color: #c4d3d9;
  box-shadow: none;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  min-height: 132px;
  overflow: visible;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.collection-landing-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.collection-landing-head > * {
  position: relative;
  z-index: 2;
}

.collection-landing-head h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.collection-landing-head p {
  color: #607d8b;
  line-height: 1.55;
  margin: 8px 0 0;
  max-width: 780px;
}

.scope-chip {
  border-radius: 18px;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.3;
  padding: 7px 12px;
  text-align: right;
}

.scope-chip em {
  color: #455a64;
  font-size: 0.76rem;
  font-style: normal;
}

.workflow-card {
  align-items: center;
  background: #ffffff;
  cursor: pointer;
  display: grid;
  gap: 14px;
  grid-template-columns: 46px 1fr auto;
  min-height: 132px;
  padding: 22px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.workflow-card + .workflow-card {
  border-top: 0;
  border-radius: 0;
}

.workflow-card:first-child:not(:last-child) {
  border-radius: 6px 6px 0 0;
}

.workflow-card:last-child:not(:first-child) {
  border-radius: 0 0 6px 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
}

.workflow-card:hover {
  background: #fbfdfd;
  border-color: #cfd8dc;
  box-shadow: 0 14px 30px -26px rgba(38, 50, 56, 0.78);
  transform: translateY(-1px);
}

.workflow-card.no-motion {
  transition: none;
}

.workflow-card.no-motion:hover {
  transform: none;
}

.workflow-icon {
  align-items: center;
  border-radius: 999px;
  display: flex;
  height: 46px;
  justify-content: center;
  width: 46px;
}

.workflow-icon i {
  font-size: 23px;
}

.workflow-copy small,
.workflow-copy strong,
.workflow-copy p {
  display: block;
}

.workflow-copy small {
  color: #607d8b;
}

.workflow-copy strong {
  color: #263238;
  font-size: 1.22rem;
  font-weight: 700;
  margin-top: 3px;
}

.workflow-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
}

.workflow-action {
  align-items: center;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 3px;
  text-transform: uppercase;
  white-space: nowrap;
}

.workflow-action i {
  font-size: 18px;
}

.accent-teal.scope-chip {
  background: #e0f2f1;
  border: 1px solid #b2dfdb;
  color: #00695c;
}

.accent-blue.scope-chip {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  color: #1565c0;
}

.accent-amber.scope-chip {
  background: #fff8e1;
  border: 1px solid #ffecb3;
  color: #ef6c00;
}

.accent-green.scope-chip {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  color: #2e7d32;
}

.accent-teal .workflow-icon {
  background: #e0f2f1;
  color: #00838f;
}

.accent-blue .workflow-icon {
  background: #e3f2fd;
  color: #1565c0;
}

.accent-amber .workflow-icon {
  background: #fff8e1;
  color: #ef6c00;
}

.accent-green .workflow-icon {
  background: #e8f5e9;
  color: #2e7d32;
}

.accent-teal .workflow-action {
  color: #00838f;
}

.accent-blue .workflow-action {
  color: #1565c0;
}

.accent-amber .workflow-action {
  color: #ef6c00;
}

.accent-green .workflow-action {
  color: #2e7d32;
}

@media only screen and (max-width: 900px) {
  .workflow-grid {
    grid-template-columns: 1fr;
  }
}

@media only screen and (max-width: 600px) {
  .collection-landing-shell {
    gap: 14px;
  }

  .collection-landing-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
    min-height: 0;
    padding: 20px;
  }

  .collection-landing-head h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .collection-landing-head p {
    font-size: 0.95rem;
  }

  .scope-chip {
    text-align: left;
    width: 100%;
  }

  .workflow-card {
    align-items: flex-start;
    grid-template-columns: 44px 1fr;
    min-height: 0;
    padding: 18px;
  }

  .workflow-copy strong {
    font-size: 1.08rem;
  }

  .workflow-copy p {
    font-size: 0.92rem;
  }

  .workflow-action {
    align-self: stretch;
    background: #f5f8f8;
    border-radius: 6px;
    grid-column: 2;
    justify-content: center;
    min-height: 38px;
    width: 100%;
  }
}
</style>
